import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaBuilding, FaCheckCircle } from 'react-icons/fa';

const Experience = () => {

  const experiences = [
      {
          "id": 1,
          "company" : "Nitwebs",
          "position" : "Senior Laravel Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "May 2024 - Aug 2025",
          "responsibilities" : [
              "Architected SaaS platform supporting thousands of concurrent users using Redis queues and event-driven architecture.",
              "Optimized MySQL queries and caching strategies, reducing system load times by 60% across high-traffic modules.",
              "Led end-to-end delivery from requirements to release, consistently shipping sprint milestones on schedule.",
              "Managed and mentored junior developers, improving code quality through peer reviews and Laravel best practices.",
              "Designed CI/CD pipelines enabling automated deployments and faster release cycles with reduced manual errors.",
              "Integrated cloud storage and third-party APIs to automate business workflows and improve operational efficiency.",
              "Collaborated with stakeholders to translate business goals into scalable, maintainable technical solutions."
          ],
          "skills_used" : ["PHP", "Laravel", "Vue.js", "Alpine.js", "Livewire", "GitHub", "DigitalOcean", "AWS S3", "MySQL", "Queue & Events", "Caching", "REST APIs"]
      },
      {
          "id": 2,
          "company" : "Code Rumble Pvt Ltd (Agile Continental Pvt Ltd)",
          "position" : "Senior Laravel Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "March 2021 - April 2024",
          "responsibilities" : [
              "Architected scalable SaaS applications with Laravel, Vue.js, and AWS to support sustained product growth.",
              "Led backend refactoring from monolithic architecture to modular Laravel services for faster feature delivery.",
              "Implemented Redis caching and MySQL optimization, reducing load times by 60% on critical user journeys.",
              "Mentored developers on clean architecture, testability, and design patterns to improve team engineering standards.",
              "Designed CI/CD pipelines using GitHub Actions, enabling automated deployments and reliable production releases."
          ],
          "skills_used" : ["Laravel", "AWS", "Redis", "MySQL Optimization", "Ajax", "CSS", "HTML", "GIT", "Jira", "Bitbucket"]
      },
      {
          "id": 3,
          "company" : "Technocrats Horizons Compusoft Pvt Ltd",
          "position" : "PHP Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "March 2018 - May 2020",
          "responsibilities" : [
              "Built e-commerce and enterprise web applications using Laravel and WordPress for diverse client requirements.",
              "Migrated legacy PHP systems to Laravel with cleaner architecture and optimized SQL for improved maintainability.",
              "Integrated payment gateways including PayPal, Stripe, and Authorize.Net to support secure online transactions.",
              "Managed deployments and server configuration on DigitalOcean to improve uptime and deployment reliability.",
              "Collaborated across product and QA teams in Agile workflows to deliver stable, testable releases."
          ],
          "skills_used" : ["PHP", "Laravel", "WordPress", "MySQL", "JavaScript", "jQuery", "Git", "Jira", "Bitbucket", "DigitalOcean", "Payment Gateways"]
      },
      {
          "id": 4,
          "company" : "Esferasoft Solutions Pvt Ltd",
          "position" : "PHP Developer",
          "location" : "Mohali, Punjab, India",
          "duration" : "November 2014 - June 2017",
          "responsibilities" : [
              "Built and customized CMS and e-commerce platforms with WordPress and Core PHP for client delivery.",
              "Delivered full-stack features including custom themes and plugins to accelerate project turnaround.",
              "Partnered with design teams to launch responsive, SEO-friendly sites with strong front-end performance.",
              "Integrated third-party APIs and tuned database queries to improve overall platform responsiveness.",
              "Maintained team workflows with Git version control, supporting consistent and traceable deployments."
          ],
          "skills_used" : ["PHP", "WordPress", "MySQL", "jQuery", "Ajax", "HTML", "CSS", "Git"]
      }
  ];

  return (
    <section id="experience" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            Professional Experience
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            A journey through 11 years of web development across multiple companies and technologies.
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-blue-200"></div>

          {/* Experience Cards */}
          <div className="space-y-14">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-6 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-md z-10"></div>

                {/* Content Card */}
                <div className={`w-full md:w-5/12 ml-16 md:ml-0 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}>
                  <div className="bg-white p-7 md:p-9 rounded-xl shadow-md hover:-translate-y-2 transition-all duration-300">
                    {/* Header */}
                    <div className="mb-6">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                        {exp.position}
                      </h3>
                      <div className="flex items-center text-blue-600 font-semibold mb-2">
                        <FaBuilding className="mr-2" />
                        <span className="text-lg">{exp.company}</span>
                      </div>
                      <div className="flex flex-col md:flex-row md:items-center text-gray-600 space-y-1 md:space-y-0 md:space-x-4">
                        <div className="flex items-center">
                          <FaCalendarAlt className="mr-2 text-sm" />
                          <span>{exp.duration}</span>
                        </div>
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="mr-2 text-sm" />
                          <span>{exp.location}</span>
                        </div>
                      </div>
                    </div>

                    {/* Responsibilities */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        Key Responsibilities:
                      </h4>
                      <ul className="space-y-3">
                        {exp.responsibilities.map((responsibility, respIndex) => (
                          <li key={respIndex} className="flex items-start">
                            <FaCheckCircle className="text-emerald-500 mt-1 mr-3 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">{responsibility}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Skills Used */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        Technologies Used:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {exp.skills_used.map((skill, skillIndex) => (
                          <span
                            key={skillIndex}
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              skillIndex % 4 === 0
                                ? 'bg-blue-100 text-blue-800'
                                : skillIndex % 4 === 1
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : skillIndex % 4 === 2
                                    ? 'bg-indigo-100 text-indigo-800'
                                    : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 bg-white p-8 rounded-xl shadow-md"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Career Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">4</div>
              <div className="text-gray-600">Companies</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">11</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">9</div>
              <div className="text-gray-600">Years Laravel</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">Team</div>
              <div className="text-gray-600">Leadership</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
